import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TeamDisplay from "@/components/TeamDisplay.jsx";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

export const DemoStepWelcome = ({ onStart, contestData }) => {
    const { t } = useTranslation();
    const { brand, welcome } = useBrandConfig();

    const [isVisible, setIsVisible] = useState(false);
    const [splashState, setSplashState] = useState('animating');

    const homeTeam = contestData?.fixture?.homeTeam || { name: "France", shortName: "FRA", logoUrl: "https://flagcdn.com/w160/fr.png" };
    const awayTeam = contestData?.fixture?.awayTeam || { name: "Argentine", shortName: "ARG", logoUrl: "https://flagcdn.com/w160/ar.png" };

    useEffect(() => {
        const exitTimer = setTimeout(() => setSplashState('exiting'), 1200);
        const doneTimer = setTimeout(() => { setSplashState('done'); setIsVisible(true); }, 1700);
        return () => { clearTimeout(exitTimer); clearTimeout(doneTimer); };
    }, []);

    const logoProps = {
        flagW: "w-20 min-[380px]:w-24",
        flagH: "h-14 min-[380px]:h-16",
        textColor: "text-black font-black text-lg min-[380px]:text-xl uppercase tracking-tighter",
    };

    const customStyles = `
        @keyframes demo-float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-8px) rotate(1deg); }
        }
        .animate-float { animation: demo-float 5s ease-in-out infinite; }

        @keyframes demo-boom {
            0% { transform: scale(0.35); opacity: 0; filter: blur(20px); }
            40% { transform: scale(1.15); opacity: 1; filter: blur(0px); }
            60% { transform: scale(0.95); }
            100% { transform: scale(1); opacity: 1; }
        }
        .animate-boom { animation: demo-boom 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }

        @keyframes demo-ping-slow {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(4); opacity: 0; }
        }
        .animate-ping-slow { animation: demo-ping-slow 1.2s cubic-bezier(0, 0, 0.2, 1) infinite; }

        @keyframes demo-flash {
            0% { transform: translateX(-200%) rotate(45deg); }
            100% { transform: translateX(200%) rotate(45deg); }
        }
        .animate-flash { animation: demo-flash 2s ease-in-out infinite; }

        .stagger-1 { transition-delay: 100ms; }
        .stagger-2 { transition-delay: 200ms; }
        .stagger-3 { transition-delay: 300ms; }
        .stagger-4 { transition-delay: 400ms; }

        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .bg-dots { background-size: 24px 24px; background-image: radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px); }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col" style={{ backgroundColor: brand.colors.pageBg }}>
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* SPLASH SCREEN */}
            {splashState !== 'done' && (
                <div
                    className={`absolute inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.9,0,0.1,1)]
                        ${splashState === 'exiting' ? 'scale-[1.5] opacity-0' : 'scale-100 opacity-100'}`}
                    style={{ backgroundColor: brand.colors.pageBg }}
                >
                    <div className="absolute w-24 h-24 bg-white rounded-full animate-ping opacity-40" />
                    <div className="absolute w-48 h-48 bg-white/30 rounded-full animate-ping-slow opacity-20" />
                    <div className="relative flex flex-col items-center">
                        <BrandLogo className="w-72 object-contain animate-boom drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)]" />
                        <div className="absolute -inset-10 bg-gradient-to-t from-transparent via-white/40 to-transparent rotate-45 animate-flash pointer-events-none" />
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className="w-full pt-16 pb-3 flex justify-center items-center z-20 relative bg-white/20 backdrop-blur-md shrink-0 border-b border-white/30">
                <BrandLogo className="h-7 object-contain" />
            </div>

            {/* CONTENU */}
            <div className="flex-1 overflow-y-auto pb-48 hide-scrollbar relative bg-dots px-6">
                <div className="flex flex-col items-center justify-center min-h-full max-w-sm mx-auto py-6 gap-8">

                    {/* Titre */}
                    <div className={`text-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-1`}>
                        <h1 className="font-heading text-[4.5rem] leading-[0.85] text-black uppercase italic tracking-tighter">
                            {t('holy.welcome.title_line1')} <br/>
                            <span className="text-white drop-shadow-md">{t('holy.welcome.title_line2')}</span>
                        </h1>
                    </div>

                    {/* PARTENAIRE */}
                    {welcome.partner && (
                        <div className={`flex justify-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-2`}>
                            <div className="bg-white/40 backdrop-blur-3xl border border-white/60 rounded-full pl-2 pr-5 py-2 flex items-center gap-3 shadow-[0_4px_15px_rgba(0,0,0,0.05)]">
                                <img
                                    src={welcome.partner.avatarUrl}
                                    alt={welcome.partner.name}
                                    className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm"
                                />
                                <div className="flex flex-col text-left justify-center">
                                    <span className="text-[9px] font-black text-black/50 uppercase tracking-[0.2em] leading-tight">
                                        {welcome.partner.label ?? t('holy.welcome.partner_label')}
                                    </span>
                                    <span className="text-lg font-heading italic uppercase text-black leading-none mt-0.5">
                                        {welcome.partner.name}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Affiche du Match */}
                    <div className={`flex items-center justify-center gap-6 w-full mt-2 transition-all duration-700 transform ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'} stagger-3`}>
                        <TeamDisplay {...logoProps} team={homeTeam} />
                        <div className="flex flex-col items-center shrink-0">
                            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-xl">
                                <span className="text-lg font-heading italic" style={{ color: brand.colors.accent }}>VS</span>
                            </div>
                        </div>
                        <TeamDisplay {...logoProps} team={awayTeam} />
                    </div>

                    {/* LOT À GAGNER */}
                    <div className={`relative w-full mt-2 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-4`}>
                        <div
                            className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 text-black font-black text-[10px] px-4 py-1 rounded-full uppercase tracking-[0.2em] shadow-sm whitespace-nowrap"
                            style={{ backgroundColor: brand.colors.highlight }}
                        >
                            {t('holy.welcome.reward_badge')}
                        </div>

                        <div className="relative overflow-hidden bg-white/30 backdrop-blur-3xl rounded-[2.5rem] p-5 shadow-[0_15px_50px_-5px_rgba(0,0,0,0.1)] border border-white/40 flex flex-row items-center justify-center gap-4">
                            {welcome.rewardImageUrl && (
                                <div className="relative flex-shrink-0 flex items-center justify-center animate-float w-1/2">
                                    <img
                                        src={welcome.rewardImageUrl}
                                        alt={welcome.rewardTitle}
                                        className="h-24 w-auto object-contain z-10 drop-shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
                                    />
                                </div>
                            )}
                            <div className="flex flex-col items-start justify-center w-1/2">
                                <h2 className="font-heading text-[3.5rem] uppercase text-black leading-none tracking-tighter italic">
                                    {welcome.rewardTitle}
                                </h2>
                                <span className="text-[9px] font-black text-black/40 uppercase tracking-widest mt-1">
                                    {welcome.rewardSubtitle}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* FOOTER CTA */}
            <div
                className={`absolute bottom-0 left-0 w-full px-6 pb-6 pt-16 to-transparent z-30 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg}, ${brand.colors.pageBg} 60%, transparent)` }}
            >
                <div className="pointer-events-auto max-w-sm mx-auto flex flex-col items-center">
                    <button
                        id="demo-btn-start"
                        onClick={onStart}
                        className="group w-full flex items-center justify-center gap-3 py-5 rounded-full font-heading text-2xl uppercase italic tracking-wider shadow-lg active:translate-y-1 active:shadow-none transition-all"
                        style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                    >
                        {t('holy.welcome.cta')}
                        <ChevronRight size={28} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <footer className="flex items-center justify-center gap-3 mt-5 opacity-60">
                        <button className="text-[9px] font-black text-black/60 uppercase tracking-[0.15em] hover:text-black transition-colors">{t('footer.rules')}</button>
                        <span className="w-1 h-1 rounded-full bg-black/20" aria-hidden="true" />
                        <button className="text-[9px] font-black text-black/60 uppercase tracking-[0.15em] hover:text-black transition-colors">{t('footer.cgps')}</button>
                        <span className="w-1 h-1 rounded-full bg-black/20" aria-hidden="true" />
                        <button className="text-[9px] font-black text-black/60 uppercase tracking-[0.15em] hover:text-black transition-colors">{t('footer.legal')}</button>
                    </footer>
                </div>
            </div>
        </div>
    );
};
